import React, { useMemo } from 'react';
import { useIntl, getLocale } from 'umi';
import { Justify, Button } from 'tea-component';
import { getCountryFlag } from '@/pages/home/_config/countries';
import SearchRecent from '../../../../components/Search/SearchRecent';
import { TABLE_SEARCH_PLACEHOLDERS } from '../../../../const';
import { IDimension } from '../../../../typings';

/**
 * 品类画像搜索组件
 * @param props 组件属性
 * @param props.marketType 市场类型
 * @param props.dimension 维度类型(countries/games/companies)
 * @param props.getPageParams 获取页面参数方法
 * @param props.searchApi 搜索接口
 * @param props.pageLogInfo 页面日志信息
 * @param props.renderTableData 渲染表格数据方法
 * @param props.getTableData 获取表格数据方法
 * @param props.resetOuterTableData 重置外部表格数据方法
 * @param props.genreName 品类名称
 * @param props.loadingDispatch loading状态派发方法
 * @param props.tableActionDispatch 表格action派发方法
 * @returns 搜索组件
 */
export default function GenreProfileSearch(props) {
  const {
    marketType,
    dimension,
    getPageParams,
    searchApi,
    pageLogInfo,
    renderTableData,
    getTableData,
    resetOuterTableData, // 清空时还原表格
    genreName, // 品类名 判断接口传参用
    loadingDispatch,
    tableActionDispatch,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const searchType = useMemo(() => {
    return `genre_stats_${marketType}_${dimension}`;
  }, [marketType, dimension]);

  const isCountriesPage = useMemo(() => {
    return dimension === 'countries';
  }, [dimension]);

  // 将表格接口返回的数据转换为组件内部需要的数据格式
  const transTableDataForSearch = (res: any) => {
    const { table = [] } = res?.data || {};
    return table?.map((item) => {
      const {
        id,
        cover,
        name,
        name_cn,
        markets,
        publisher,
        name_abbr,
        location_city,
        location_country,
      } = item;
      // 格式化成组件内部需要的格式
      // 参数isCustomRendering可以控制渲染类型
      if (isCountriesPage) {
        return {
          ...item,
          name,
          name_cn,
          icon: getCountryFlag(name_abbr),
        };
      }
      return {
        ...item,
        imgSrc: cover,
        lineOneStr: name_cn || name,
        lineTwoImg: getCountryFlag(markets),
        lineTwoStr: publisher ?? `${location_city},${location_country}`,
      };
    });
  };

  // 将搜索记录的接口返回数据转换为组件内部需要的数据格式
  const transRecordDataForSearch = (res: any) => {
    const result = res?.data || [];
    return result?.map((item) => {
      const { company_name, country_abbre, metric_cover_url, metric_id, metric_name } = item;
      const flag = getCountryFlag(country_abbre);
      if (isCountriesPage)
        return {
          ...item,
          name: metric_name,
          name_cn: metric_name,
          icon: getCountryFlag(country_abbre),
        };
      return {
        ...item,
        imgSrc: metric_cover_url,
        // 搜索的时候将值展示在input上
        name: metric_name,
        lineOneStr: metric_name,
        lineTwoImg: flag,
        lineTwoStr: company_name,
      };
    });
  };

  // 将接口返回的搜索结果（表格数据）转换为添加搜索记录接口所需的数据格式
  const getAddRecordParams = (item) => {
    if (isCountriesPage) {
      const { name, name_cn, name_abbr } = item;
      return {
        metric_name: name,
        metric_name_cn: name_cn,
        country_abbre: name_abbr,
      };
    }
    const { id, cover, name, name_cn, markets, publisher, location_city, location_country } = item;
    return {
      company_name: publisher ?? `${location_city},${location_country}`,
      country_abbre: markets,
      metric_cover_url: cover,
      metric_id: id,
      metric_name: name,
    };
  };

  // 将组件返回的数据转换为删除记录接口所需的数据格式
  const getDeleteRecordParams = (item) => {
    const { lineOneStr, name } = item;
    if (isCountriesPage)
      return {
        name,
      };
    return {
      name: lineOneStr,
    };
  };

  // 调用设置表格的方法，将搜索结果展示在前端
  const setTableSearchResult = (item: any) => {
    // 搜索下拉框点击选择item后，需要传select_code
    const metric_condition: any = {};
    if (['games'].includes(dimension)) {
      metric_condition.game_ids = [item?.id];
    }
    if (['companies'].includes(dimension)) {
      metric_condition.publisher_ids = [item?.publisher_id];
    }
    if (dimension === 'countries') {
      metric_condition.market_keyword = item?.name_abbr;
      // selectItem = item?.country_abbre || item?.name_abbr || '';
    }
    const modifiedParams = getPageParams({ metric_condition }, 'click');
    tableActionDispatch({
      type: 'pageIndex',
      payload: 1,
    });
    getTableData({
      modifiedParams: {
        ...modifiedParams,
        page: 1,
      },
    });
  };

  const getDataNameKey = (_dimension: IDimension) => {
    switch (_dimension) {
      case 'countries':
        return 'country_abbre';
      case 'games':
        return 'metric_id';
      case 'companies':
        return 'metric_id';
      default:
        return 'name';
    }
  };

  const resetTableData = () => {
    resetOuterTableData();
  };
  const tablePlaceholder = useMemo(() => {
    const searchPlaceholderLocaleKey = TABLE_SEARCH_PLACEHOLDERS[dimension] ?? 'dbi_label_search';
    return formatMessage({ id: searchPlaceholderLocaleKey });
  }, [locale, dimension]);

  return (
    <Justify
      className="dbi-card-justify"
      right={
        <>
          <SearchRecent
            key={searchType}
            searchType={searchType}
            isOuterSearchKey={true}
            dataNameKey={getDataNameKey(dimension)}
            fetchParams={getPageParams}
            searchListApi={searchApi}
            pageLogInfo={pageLogInfo}
            transTableDataForSearch={transTableDataForSearch}
            transRecordDataForSearch={transRecordDataForSearch}
            getTableResultList={setTableSearchResult}
            handleClear={resetTableData}
            placeholder={tablePlaceholder}
            isCustomRendering={!!isCountriesPage}
            getAddRecordParams={getAddRecordParams}
            getDeleteRecordParams={getDeleteRecordParams}
            genreName={genreName}
            dimension={dimension}
            loadingDispatch={loadingDispatch}
          />
        </>
      }
    />
  );
}
